import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Loader2, Gift } from "lucide-react";
import api from "../lib/api";
import { SUBSCRIPTION_TIERS } from "../data/content";
import { Ornament } from "../components/Ornament";

export default function SubscribePage() {
  const navigate = useNavigate();
  // Payments are disabled pre-launch. Route users to the Founding Beta invitation instead.
  useEffect(() => {
    navigate("/founding-beta", { replace: true });
  }, [navigate]);
  return null;
}

function _SubscribePageDisabled() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(params.get("plan") || "individual");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState("");

  useEffect(() => {
    (async () => {
      try { await api.get("/auth/me"); } catch (e) {
        const next = `/subscribe?plan=${encodeURIComponent(plan)}`;
        navigate(`/login?next=${encodeURIComponent(next)}`);
      }
    })();
    api.get("/stats/founding").then((r) => setStats(r.data)).catch(() => {});
  }, [navigate, plan]);

  const foundingOpen = stats?.available !== false;
  const remaining = stats?.remaining;

  const checkout = async () => {
    setBusy(true); setError("");
    try {
      const res = await api.post("/subscriptions/checkout", { sku: plan, origin_url: window.location.origin });
      if (res.data?.url) window.location.href = res.data.url;
      else setError("Could not start checkout.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  const redeem = async (e) => {
    e?.preventDefault?.();
    setRedeeming(true); setRedeemMsg("");
    try {
      const res = await api.post("/gifts/redeem", { code: redeemCode.trim() });
      setRedeemMsg(`Beautifully done. Your ${res.data.tier === "family" ? "Family" : "Living Book"} subscription is now active.`);
      setTimeout(() => navigate("/dashboard"), 2200);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setRedeemMsg(typeof d === "string" ? d : "That code wasn't recognised.");
    } finally { setRedeeming(false); }
  };

  const selectedTier = SUBSCRIPTION_TIERS[plan];
  const selectedVariant = foundingOpen ? selectedTier.founding : selectedTier.standard;

  return (
    <div className="min-h-screen paper" data-testid="subscribe-page">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="back-link">
          <ChevronLeft size={16} /> Back
        </Link>
        <Link to="/gift" className="btn-outline-navy flex items-center gap-2" data-testid="gift-link">
          <Gift size={16} /> Gift a membership
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <div className="text-center mb-12">
          <p className="label-ui text-[11px] text-[#B8973A]">Story Keepers Membership</p>
          <h1 className="text-4xl sm:text-5xl mt-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Unlock <span style={{ fontStyle: "italic" }}>the whole book.</span>
          </h1>
          <Ornament className="my-8" />
          <p className="italic text-[#8C8278] max-w-2xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
            Voice, photos, unlimited follow-ups, your full book preview — and 20–25% off every printed book.
          </p>
          {foundingOpen ? (
            <p className="mt-6 label-ui text-[12px] text-[#B8973A]" data-testid="founding-counter">
              ✦ 500 Founding Memberships available.
            </p>
          ) : (
            <p className="mt-6 label-ui text-[12px] text-[#8C8278]" data-testid="founding-closed">
              Founding membership is now closed. Standard pricing applies.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {Object.values(SUBSCRIPTION_TIERS).map((t, i) => {
            const active = plan === t.sku;
            const variant = foundingOpen ? t.founding : t.standard;
            const standardPrice = t.standard.price;
            const isFamily = t.sku === "family";
            const displayName = isFamily ? "Family Story Keepers" : "Individual Story Keeper";
            const displayHeadline = isFamily
              ? "For the whole family to share."
              : "Your Living Book, kept and continued.";
            return (
              <button
                key={t.sku}
                type="button"
                onClick={() => setPlan(t.sku)}
                className={`text-left p-8 sm:p-10 transition-all duration-500 relative ${active ? "bg-white" : "bg-[#FAF7F2] hover:bg-white"}`}
                style={{ borderRadius: 4, border: active ? "1px solid #B8973A" : "1px solid #F0EAE0" }}
                data-testid={`subscribe-card-${t.sku}`}
              >
                {variant.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 label-ui text-[10px] whitespace-nowrap" style={{ background: "#B8973A", color: "#FAF7F2", borderRadius: 2 }}>
                    {variant.badge}
                  </div>
                )}
                <p className="chapter-roman">{i === 0 ? "I" : "II"}</p>
                <h3 className="text-2xl mt-3 mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>{displayName}</h3>
                <p className="italic text-[#8C8278] mb-5" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>{displayHeadline}</p>
                <div className="text-4xl mb-1" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  £{variant.price}<span className="text-base text-[#8C8278]"> / year</span>
                </div>
                {foundingOpen && (
                  <p className="label-ui text-[10px] text-[#B8973A] mb-5">Founding rate · your price stays the same while your membership remains active · normally £{standardPrice}</p>
                )}
                <ul className="space-y-2 mt-4">
                  {t.features.map((f, j) => (
                    <li key={j} className="text-sm text-[#1B2A4A] flex items-start gap-3" style={{ fontFamily: "Georgia, serif", lineHeight: 1.6 }}>
                      <span className="text-[#B8973A] mt-0.5">✦</span><span>{f}</span>
                    </li>
                  ))}
                </ul>
                <p className="label-ui text-[10px] text-[#B8973A] mt-6">{active ? "Selected" : "Choose"}</p>
              </button>
            );
          })}
        </div>

        <div className="bg-white hairline-cream p-8 max-w-2xl mx-auto text-center" style={{ borderRadius: 4 }}>
          {error && <p className="text-red-700 text-sm mb-4" data-testid="subscribe-error">{error}</p>}
          <button onClick={checkout} disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="subscribe-checkout-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            Continue to secure payment · £{selectedVariant.price} / year
          </button>
          <p className="text-xs text-[#8C8278] italic mt-3" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "0.95rem" }}>
            Your Story Week responses remain in your account whether or not you become a Story Keeper.
          </p>
        </div>

        {/* Redeem a gift */}
        <div className="max-w-2xl mx-auto mt-16 text-center">
          <Ornament className="my-6" />
          <p className="label-ui text-[11px] text-[#B8973A] mb-3">Have a gift code?</p>
          <form onSubmit={redeem} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="redeem-form">
            <input
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              placeholder="GIFT_…"
              className="flex-1 px-4 py-3 hairline-cream bg-white"
              style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", minHeight: 48 }}
              data-testid="redeem-code-input"
            />
            <button type="submit" disabled={redeeming} className="btn-outline-navy" data-testid="redeem-submit-btn">
              {redeeming ? "…" : "Redeem"}
            </button>
          </form>
          {redeemMsg && <p className="mt-4 italic text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif" }} data-testid="redeem-msg">{redeemMsg}</p>}
        </div>
      </main>
    </div>
  );
}
